import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { DialogPanel } from "../shared/DialogPanel";
import { backdropClose } from "../shared/backdrop";

export type GuardState = { dirty: boolean; save: () => Promise<unknown> };
export type LeaveController = { attemptLeave: (run: () => void) => void };

type GuardCtxT = { report: (s: GuardState) => void; attemptLeave: (run: () => void) => void };
const GuardCtx = createContext<GuardCtxT | null>(null);

// Save bloqueado por validação — rejeita pra o popup mostrar erro em vez de persistir lixo.
export const blockedSave = () => Promise.reject(new Error("invalid"));

const ghost: React.CSSProperties = { padding: "8px 16px", background: "var(--surface-input)", border: "1px solid var(--line-strong)", borderRadius: 8, color: "var(--ink-secondary)", fontSize: 13, cursor: "pointer" };

/**
 * Host do aviso de "alterações não salvas". Coloque acima das abas de config e de
 * qualquer gatilho de saída (troca de aba, fechar modal, navegar). A aba ativa
 * registra {dirty, save} via useUnsavedGuard; os gatilhos chamam attemptLeave.
 */
export function UnsavedGuard({ children, controllerRef }: { children: ReactNode; controllerRef?: React.MutableRefObject<LeaveController> }) {
  const guardRef = useRef<GuardState>({ dirty: false, save: async () => {} });
  const report = useCallback((s: GuardState) => { guardRef.current = s; }, []);
  const [pending, setPending] = useState<null | { run: () => void }>(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(false);

  const attemptLeave = useCallback((run: () => void) => {
    if (guardRef.current.dirty) { setErr(false); setPending({ run }); }
    else run();
  }, []);

  // Expõe attemptLeave pro componente que renderiza o host (não dá pra consumir o
  // próprio contexto que ele provê).
  useEffect(() => { if (controllerRef) controllerRef.current = { attemptLeave }; }, [controllerRef, attemptLeave]);

  function close() { setPending(null); setSaving(false); setErr(false); }
  function discard() { const p = pending; close(); p?.run(); }
  async function doSave() {
    setSaving(true); setErr(false);
    try { await guardRef.current.save(); } catch { setSaving(false); setErr(true); return; }
    const p = pending; close(); p?.run();
  }

  return (
    <GuardCtx.Provider value={{ report, attemptLeave }}>
      {children}
      {pending && (
        <div {...backdropClose(() => { close(); }, { guarded: true })} style={{ position: "fixed", inset: 0, background: "var(--overlay)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: "calc(var(--z-modal) + 100)" as any }}>
          <DialogPanel onClose={() => close()} role="alertdialog" label="Salvar alterações antes de sair?" style={{ background: "var(--surface-app)", border: "1px solid var(--line)", borderRadius: 12, padding: 24, width: 440, display: "flex", flexDirection: "column", gap: 16 }}>
            <span style={{ fontSize: 15, fontWeight: 600, color: "var(--ink)" }}>Salvar alterações antes de sair?</span>
            <span style={{ fontSize: 13, color: "var(--ink-secondary)" }}>Há modificações não salvas nesta aba.</span>
            {err && <span style={{ fontSize: 12, color: "var(--danger)" }}>Erro ao salvar. Verifique os campos e tente novamente.</span>}
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button onClick={close} disabled={saving} style={ghost}>Cancelar</button>
              <button onClick={discard} disabled={saving} style={{ ...ghost, color: "var(--danger)" }}>Descartar</button>
              <button onClick={doSave} disabled={saving} style={{ padding: "8px 16px", background: "var(--brand)", border: "none", borderRadius: 8, color: "#fff", fontSize: 13, cursor: "pointer" }}>{saving ? "Salvando..." : "Salvar"}</button>
            </div>
          </DialogPanel>
        </div>
      )}
    </GuardCtx.Provider>
  );
}

/** A aba ativa reporta se tem alterações não salvas e como salvá-las. No-op sem host. */
export function useUnsavedGuard(dirty: boolean, save: () => Promise<unknown>) {
  const report = useContext(GuardCtx)?.report;
  useEffect(() => {
    if (!report) return;
    report({ dirty, save });
    return () => report({ dirty: false, save: async () => {} });
  }, [report, dirty, save]);
}

/** Gatilho de saída. Sem host, executa direto (sem aviso). */
export function useAttemptLeave(): (run: () => void) => void {
  const ctx = useContext(GuardCtx);
  return ctx?.attemptLeave ?? ((run) => run());
}
